/**
 * Processes the new password and provide the link to redirect the user to the right application.
 */
class ResetPasswordContainer {
  /**
   * @param {ngQ}                   $q                To create an already rejected promise. This
   *                                                  is used when the request is made while
   *                                                  another is already being processed.
   * @param {FrontendConfiguration}  appConfiguration   To get the Olapic's privacy policy URL.
   * @param {AppErrorHandler}       appErrorHandler   To silently handle the error in the case the
   *                                                  request for the referrer fails.
   * @param {Auth}                  auth              To actually reset the user password and, if
   *                                                  needed, get the referrer information.
   * @param {AuthRoutesManager}     authRoutesManager To get the route to login screen and use it
   *                                                  on the _"go back_ link default value.
   * @param {Passwords}             passwords         To validate the new password requirements.
   */
  constructor(
    $q,
    appConfiguration,
    appErrorHandler,
    auth,
    authRoutesManager,
    passwords,
  ) {
    'ngInject';

    /**
     * A local reference for the `$q` service.
     * @type {ngQ}
     */
    this.$q = $q;
    /**
     * A local reference for the `appErrorHandler` service.
     * @type {AppErrorHandler}
     */
    this.appErrorHandler = appErrorHandler;
    /**
     * A local reference for the `auth` service.
     * @type {Auth}
     */
    this.auth = auth;
    /**
     * A local reference for the `authRoutesManager` service.
     * @type {AuthRoutesMan}
     */
    this.authRoutesManager = authRoutesManager;
    /**
     * A local reference for the `passwords` service.
     * @type {Passwords}
     */
    this.passwords = passwords;
    /**
     * The Olapic's privacy policy URL, to show on a link.
     * @type {string}
     */
    this.privacyPolicyUrl = appConfiguration.privacyPolicyURL;
    /**
     * The URL for the _"go back"_ link.
     * @type {string}
     */
    this.goBackUrl = '';
    /**
     * Once the information is submitted and validated, in case of error, this property will be
     * used to store the message and send it to the child component.
     * @type {string}
     */
    this.error = '';
    /**
     * Whether or not the container is processing the form.
     * @type {boolean}
     */
    this.loading = false;
    /**
     * Whether or not the process finished.
     * @type {boolean}
     */
    this.success = false;
  }
  /**
   * When the container gets mounted, set the default value of the _"go back"_ link as the one
   * for the login screen; then it checks if a referrer code was recevied and if it was, requests
   * the referrer information in order to overwrite the link.
   */
  $onInit() {
    this.goBackUrl = this.authRoutesManager.login();
    if (this.refCode) {
      this._loadRef();
    }
  }
  /**
   * Processes the form. This is a callback sent to the child component.
   * @param {string} password The new password for the user.
   * @return {Promise<undefined,Error>}
   */
  resetPassword(password) {
    let result;
    if (this.loading) {
      // Reject the promise if the container is already processing the form.
      result = this.$q.reject(new Error('The request is in progress'));
    } else {
      // Reset any possible error.
      this.error = '';
      // Turn on the _"processing flag"_.
      this.loading = true;
      // Make the request to set the password.
      result = this.auth.resetPassword(this.code, password)
      .then(() => {
        // If everything went well, mark the `success` flag.
        this.success = true;
      })
      .catch((error) => {
        // If something went wrong, get the error message for the child component to show.
        this.error = error.message;
      })
      .then(() => {
        // Turn off the _"processing flag"_.
        this.loading = false;
      });
    }
    // Return the promise.
    return result;
  }
  /**
   * Validates if a new password meets the service requeriments. This is a callback sent to the
   * child component.
   * @param {string} password The password to validate.
   * @return {string} The service returns an empty string if the validations passed, or an error
   *                  message if something went wrong.
   */
  validatePassword(password) {
    return this.passwords.validate(password);
  }
  /**
   * Requests the URL of the application from where the user started the process of resetting
   * his/her password. The returned URL will be stored on the `goBackUrl` property.
   * @return {Promise<undefined,Error>}
   * @access protected
   * @ignore
   */
  _loadRef() {
    let result;
    if (this.loading) {
      // Reject the promise if the container is already making a request.
      result = this.$q.reject(new Error('The referrer is already being requested'));
    } else {
      // Turn on the _"processing flag"_.
      this.loading = true;
      // Request the referrer information.
      result = this.auth.getReferrerURL(this.code, this.refCode)
      .then((url) => {
        // Overwrite the _"go back"_ link.
        this.goBackUrl = url;
      })
      .catch((error) => {
        /**
         * Since this is only used to display a link, if the request fails, don't show the
         * error message to the user, just tell the error handler to silently log it on the
         * console.
         */
        this.appErrorHandler.silent(error);
      })
      .then(() => {
        // Turn off the _"processing flag"_.
        this.loading = false;
      });
    }

    return result;
  }
}
/**
 * Shows and handles the logic of the screen that allows the user to create a new password. This
 * is used both when the user is creating the password for the first time and when the user is
 * resetting a forgotten password.
 * @ngesdoc component
 * @ngname resetPasswordContainer
 * @ngcontroller {@link ResetPasswordContainer}
 * @ngcontainer
 * @ngmodule auth
 * @ngbinding {?boolean} create  Whether the user is creating its first password or resetting it.
 * @ngbinding {string}   code    The secret code the user received in his/her email.
 * @ngbinding {?string}  refCode A referrer code that allows the app to get the URL of the
 *                               application from where the user starting the processe to reset
 *                               the password. This URL will be used on the _"go back"_ link.
 */
export default {
  controller: ResetPasswordContainer,
  template: `
    <reset-password
      create="$ctrl.create"
      error="$ctrl.error"
      loading="$ctrl.loading"
      success="$ctrl.success"
      on-reset="$ctrl.resetPassword(password)"
      go-back-url="$ctrl.goBackUrl"
      privacy-policy-url="$ctrl.privacyPolicyUrl"
      validate-password="$ctrl.validatePassword(password)"
    ></reset-password>
  `,
  bindings: {
    create: '<',
    code: '@',
    refCode: '@',
  },
};
